import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge, Row, Col } from 'react-bootstrap';
import { Clock, RefreshCw, ExternalLink, PaintRoller, Cpu, Coins, MessageSquare } from 'lucide-react';
import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { UpcomingMaintenanceItem } from '../../../hooks/management/useUpcomingMaintenance';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { useUsersData } from '../../../Helpers/UsersContext';
import { MaintenanceIntervalType } from '../../../types/Enums';
import { formatDateForDisplay, getDaysUntilDue } from '../../../Helpers/DateUtils';
import { BuildingSummary } from '../../../types/protocols';
import { FirebaseImage, ImageSize } from '../../../Helpers/ImageLoader';
import { getCoverPhotoStoragePath } from '../../../Helpers/utils';
import { placeholderForBuildingType } from '../../../Helpers/PlaceholderUtils';
import MiniMap from '../../../Components/MiniMap';
import { SnoozeModal } from '../../../Components/SnoozeModal';
import up_right_arrow from '../../../Images/button-icons/up_right_arrow.png';
import './MaintenanceDetailModal.css';

interface MaintenanceDetailModalProps {
    show: boolean;
    onHide: () => void;
    item: UpcomingMaintenanceItem;
    onConvert: () => void;
    onSnooze: (snoozeDate: string) => Promise<void>;
    onUnsnooze: () => Promise<void>;
}

function getUrgencyBadge(daysUntil: number): { variant: string; label: string } | null {
    if (daysUntil <= 0) return { variant: 'danger', label: 'Förfallen' };
    if (daysUntil <= 30) return { variant: 'warning', label: 'Inom 30 dagar' };
    if (daysUntil <= 90) return { variant: 'info', label: 'Inom 3 mån' };
    return null;
}

function getOrdinalSuffix(n: number): string {
    return (n % 10 === 2 && n % 100 !== 12) ? ':a' : ':e';
}

function formatInterval(intervalType: MaintenanceIntervalType, intervalValue: number): string {
    const typeLabels: Record<MaintenanceIntervalType, { singular: string; neuter: boolean }> = {
        [MaintenanceIntervalType.days]: { singular: 'dag', neuter: false },
        [MaintenanceIntervalType.weeks]: { singular: 'vecka', neuter: false },
        [MaintenanceIntervalType.months]: { singular: 'månad', neuter: false },
        [MaintenanceIntervalType.years]: { singular: 'år', neuter: true }
    };

    const label = typeLabels[intervalType] || { singular: 'år', neuter: true };

    if (intervalValue === 1) return `Varje ${label.singular}`;
    if (intervalValue === 2) return `${label.neuter ? 'Vartannat' : 'Varannan'} ${label.singular}`;
    const prefix = label.neuter ? 'Vart' : 'Var';
    return `${prefix} ${intervalValue}${getOrdinalSuffix(intervalValue)} ${label.singular}`;
}

const MaintenanceDetailModal: React.FC<MaintenanceDetailModalProps> = ({
    show,
    onHide,
    item,
    onConvert,
    onSnooze,
    onUnsnooze
}) => {
    const [showSnoozeModal, setShowSnoozeModal] = useState(false);
    const [snoozing, setSnoozing] = useState(false);
    const [unsnoozing, setUnsnoozing] = useState(false);
    const [buildingSummary, setBuildingSummary] = useState<BuildingSummary | null>(null);
    const { users } = useUsersData();
    const auth = getAuth();
    const { getTemplateById, getTranslation } = useBuildComponentTypes();

    const isAdmin = users?.find(u => u.key === auth.currentUser?.uid)?.admin || false;

    const template = getTemplateById(item.templateId);
    const activityName = template?.name || item.activityName;
    const componentTypeName = item.componentType ? getTranslation(item.componentType) : null;

    const daysUntil = getDaysUntilDue(item.nextDate);
    const urgency = getUrgencyBadge(daysUntil);
    const isOverdue = daysUntil <= 0;
    const isSnoozed = !!item.snoozedUntil;

    // Fetch building summary for thumbnail and map
    useEffect(() => {
        if (!show || !item.buildingKey) {
            setBuildingSummary(null);
            return;
        }
        const fetch = async () => {
            try {
                const db = getDatabase();
                const snap = await get(ref(db, `buildingsSummary/${item.buildingKey}`));
                if (snap.exists()) setBuildingSummary(snap.val() as BuildingSummary);
            } catch (err) {
                console.error('Error fetching building summary:', err);
            }
        };
        fetch();
    }, [show, item.buildingKey]);

    const effectiveCoordinates = buildingSummary?.coordinates;
    const hasCoordinates = !!(effectiveCoordinates?.latitude && effectiveCoordinates?.longitude);
    const buildingInfo = buildingSummary?.buildingInfo;
    const googleMapsUrl = hasCoordinates
        ? `https://maps.google.com/?q=${effectiveCoordinates!.latitude},${effectiveCoordinates!.longitude}`
        : buildingInfo?.street
            ? `https://maps.google.com/search/${encodeURIComponent([buildingInfo.street, buildingInfo.postalCode, buildingInfo.city].filter(Boolean).join(' '))}`
            : null;

    const buildingLink = `/building/${item.buildingKey}`;

    const handleConvert = () => {
        onHide();
        onConvert();
    };

    const handleSnooze = async (snoozeDate: string) => {
        setSnoozing(true);
        try {
            await onSnooze(snoozeDate);
            setShowSnoozeModal(false);
            onHide();
        } finally {
            setSnoozing(false);
        }
    };

    const handleUnsnooze = async () => {
        setUnsnoozing(true);
        try {
            await onUnsnooze();
            onHide();
        } finally {
            setUnsnoozing(false);
        }
    };

    return (
        <>
            <Modal show={show} onHide={onHide} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center gap-2">
                        <Badge bg="" className="badge-underhall d-inline-flex align-items-center gap-1">
                            <PaintRoller size={10} />
                            Underhåll
                        </Badge>
                        {urgency && (
                            <Badge
                                bg={urgency.variant === 'danger' ? '' : urgency.variant}
                                className={urgency.variant === 'danger' ? 'badge-overdue' : 'badge-label'}
                            >
                                {urgency.label}
                            </Badge>
                        )}
                        {isSnoozed && (
                            <Badge bg="secondary" className="badge-label">
                                Snoozad
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="mb-4">
                        {/* Left: activity name, component, interval, cost */}
                        <Col md={6} className="mb-3 mb-md-0">
                            <h5 style={{ fontSize: 20 }} className="mb-4">{activityName}</h5>

                            {(componentTypeName || item.componentLabel) && (
                                <div className="mt-3">
                                    <h6 className="maintenance-modal__section-title">
                                        <Cpu size={16} className="text-muted" />
                                        Komponent
                                    </h6>
                                    {componentTypeName && <div>{componentTypeName}</div>}
                                    {item.componentLabel && (
                                        <div className="text-muted small">{item.componentLabel}</div>
                                    )}
                                </div>
                            )}

                            <div className="mt-3">
                                <h6 className="maintenance-modal__section-title">
                                    <RefreshCw size={16} className="text-muted" />
                                    Intervall
                                </h6>
                                <div>{formatInterval(item.intervalType, item.intervalValue)}</div>
                            </div>

                            {item.cost !== undefined && item.cost > 0 && (
                                <div className="mt-3">
                                    <h6 className="maintenance-modal__section-title">
                                        <Coins size={16} className="text-muted" />
                                        Uppskattad kostnad
                                    </h6>
                                    <div>{item.cost.toLocaleString('sv-SE')} kr</div>
                                </div>
                            )}

                            {item.comment && (
                                <div className="mt-3">
                                    <h6 className="maintenance-modal__section-title">
                                        <MessageSquare size={16} className="text-muted" />
                                        Kommentar
                                    </h6>
                                    <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.comment}</div>
                                </div>
                            )}

                            {isSnoozed && item.snoozedUntil && (
                                <div className="alert alert-secondary mt-3 mb-0">
                                    <small>Snoozad till {formatDateForDisplay(item.snoozedUntil)}</small>
                                </div>
                            )}
                        </Col>

                        {/* Right: building card + map */}
                        <Col md={6}>
                            <div className="maintenance-modal__building-card">
                            <div className="d-flex align-items-center gap-3 mb-2">
                                <FirebaseImage
                                    storagePath={buildingSummary ? getCoverPhotoStoragePath(buildingSummary) : undefined}
                                    size={ImageSize.THUMBNAIL_SMALL}
                                    placeholder={placeholderForBuildingType(buildingSummary?.buildingType)}
                                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                                    alt={item.buildingName}
                                />
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                    <strong>{item.buildingName}</strong>
                                    {buildingInfo?.street && (
                                        <div className="small text-muted mt-1">{buildingInfo.street}</div>
                                    )}
                                    {(buildingInfo?.postalCode || buildingInfo?.city) && (
                                        <div className="small text-muted">
                                            {[buildingInfo?.postalCode, buildingInfo?.city].filter(Boolean).join(' ')}
                                        </div>
                                    )}
                                </div>
                                {item.buildingKey && buildingSummary && (
                                    <a
                                        href={buildingLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="maintenance-modal__building-link-btn"
                                    >
                                        <ExternalLink size={16} />
                                    </a>
                                )}
                            </div>

                            {hasCoordinates && (
                                <div className="mt-3">
                                    <MiniMap
                                        coordinates={effectiveCoordinates!}
                                        buildingName={item.buildingName}
                                        height={160}
                                    />
                                </div>
                            )}

                            {googleMapsUrl && (
                                <Button
                                    variant="secondary"
                                    className="mt-2"
                                    style={{
                                        fontSize: '14px',
                                        backgroundColor: '#001D60',
                                        borderRadius: '14px',
                                        padding: '2px 40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: 'none',
                                    }}
                                    onClick={() => window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')}
                                >
                                    Google Maps
                                    <img src={up_right_arrow} alt="" style={{ width: 14, height: 14, marginLeft: 3, filter: 'brightness(0) invert(1)' }} />
                                </Button>
                            )}
                            </div>
                        </Col>
                    </Row>

                    {/* Due date */}
                    <div className="border-top pt-3 d-flex align-items-center gap-2" style={isOverdue ? { color: '#dc3545' } : { color: '#6c757d' }}>
                        <Clock size={16} />
                        <span>{isOverdue ? 'Förföll' : 'Förfaller'}: {formatDateForDisplay(item.nextDate)}</span>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    {isAdmin && !isSnoozed && (
                        <Button
                            variant="outline-secondary"
                            onClick={() => setShowSnoozeModal(true)}
                            disabled={snoozing}
                        >
                            Snooza
                        </Button>
                    )}
                    {isAdmin && isSnoozed && (
                        <Button
                            variant="outline-warning"
                            onClick={handleUnsnooze}
                            disabled={unsnoozing}
                        >
                            {unsnoozing ? 'Avsnoozar...' : 'Avsnooza'}
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        style={{
                            backgroundColor: '#001D60',
                            borderColor: '#001D60',
                        }}
                        onClick={handleConvert}
                    >
                        Skapa ärende
                    </Button>
                </Modal.Footer>
            </Modal>

            <SnoozeModal
                show={showSnoozeModal}
                onHide={() => setShowSnoozeModal(false)}
                itemName={activityName}
                onSnooze={handleSnooze}
            />
        </>
    );
};

export default MaintenanceDetailModal;
